import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { useAccount, useConnect, useDisconnect } from 'wagmi';

// Types
interface WalletState {
  address: string | null;
  isConnected: boolean;
  isLoading: boolean;
  error: string | null;
  providerConflict: boolean;
}

interface WalletContextType extends WalletState {
  connect: (connector: any) => void;
  disconnect: () => void;
  clearError: () => void;
}

// Action types
type WalletAction =
  | { type: 'SET_ADDRESS'; payload: string | null }
  | { type: 'SET_CONNECTED'; payload: boolean }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_PROVIDER_CONFLICT'; payload: boolean }
  | { type: 'CLEAR_ERROR' };

// Initial state
const initialState: WalletState = {
  address: null,
  isConnected: false,
  isLoading: false,
  error: null,
  providerConflict: false,
};

// Reducer
const walletReducer = (state: WalletState, action: WalletAction): WalletState => {
  switch (action.type) {
    case 'SET_ADDRESS':
      return { ...state, address: action.payload };
    case 'SET_CONNECTED':
      return { ...state, isConnected: action.payload };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_PROVIDER_CONFLICT':
      return { ...state, providerConflict: action.payload };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    default:
      return state;
  }
};

// Context
const WalletContext = createContext<WalletContextType | undefined>(undefined);

// Provider component
interface WalletProviderProps {
  children: ReactNode;
}

export const WalletProvider: React.FC<WalletProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(walletReducer, initialState);
  const { address, isConnected } = useAccount();
  const { connect, connectors } = useConnect();
  const { disconnect: wagmiDisconnect } = useDisconnect();

  // Sync wallet state with wagmi
  useEffect(() => {
    dispatch({ type: 'SET_ADDRESS', payload: address || null });
    dispatch({ type: 'SET_CONNECTED', payload: isConnected });
    dispatch({ type: 'SET_LOADING', payload: false });
  }, [address, isConnected]);

  // Handle provider conflicts
  useEffect(() => {
    const handleProviderConflict = () => {
      if (window.ethereum && window.ethereum.isMetaMask) {
        console.log('MetaMask provider detected');
        dispatch({ type: 'SET_PROVIDER_CONFLICT', payload: false });
      }
    };

    const handleProviderChange = () => {
      console.log('Provider changed, reconnecting...');
    };

    if (window.ethereum) {
      window.ethereum.on('chainChanged', handleProviderChange);
      window.ethereum.on('accountsChanged', handleProviderChange);
    }

    handleProviderConflict();

    return () => {
      if (window.ethereum) {
        window.ethereum.removeListener('chainChanged', handleProviderChange);
        window.ethereum.removeListener('accountsChanged', handleProviderChange);
      }
    };
  }, []);

  // Enhanced error handling for provider conflicts
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      if (event.error && event.error.message) {
        const message = event.error.message;
        if (message.includes('MetaMask encountered an error setting the global Ethereum provider') ||
            message.includes('Cannot set property ethereum')) {
          console.warn('MetaMask provider conflict detected, attempting to resolve...');
          dispatch({ type: 'SET_PROVIDER_CONFLICT', payload: true });
          
          // Try to resolve the conflict by reconnecting
          setTimeout(() => {
            if (isConnected) {
              wagmiDisconnect();
              setTimeout(() => {
                // Attempt to reconnect
                const metaMaskConnector = connectors.find(c => c.id === 'metaMask');
                if (metaMaskConnector) {
                  connect({ connector: metaMaskConnector });
                }
              }, 1000);
            }
          }, 2000);
        }
      }
    };

    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, [isConnected, wagmiDisconnect, connect, connectors]);

  // Connect function
  const handleConnect = (connector: any) => {
    dispatch({ type: 'CLEAR_ERROR' });
    connect({ connector });
  };

  // Disconnect function
  const handleDisconnect = () => {
    dispatch({ type: 'CLEAR_ERROR' });
    wagmiDisconnect();
  };

  // Clear error
  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const value: WalletContextType = {
    ...state,
    connect: handleConnect,
    disconnect: handleDisconnect,
    clearError,
  };

  return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>;
};

// Hook to use wallet context
export const useWallet = (): WalletContextType => {
  const context = useContext(WalletContext);
  if (context === undefined) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return context;
}; 